import React, { useState } from "react";
import { Dialog } from "@headlessui/react";
import {
  XMarkIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/solid";

interface UploadGuideDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function UploadGuideDialog({
  isOpen,
  onClose,
}: UploadGuideDialogProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = () => {
    if (dontShowAgain) {
      // Save preference in localStorage
      localStorage.setItem("hideUploadGuideTutorial", "true");
    }
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className="relative z-[70]" // Higher z-index than the main dialog
    >
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden transform transition-all">
          {/* Header with gradient background */}
          <div className="bg-gradient-to-r from-purple-600/90 to-indigo-600/90 px-6 py-4">
            <div className="flex justify-between items-center">
              <Dialog.Title className="text-lg font-semibold text-white">
                <div className="flex items-center">
                  <InformationCircleIcon className="h-6 w-6 text-white/90 mr-2" />
                  <span className="text-xl">Product Upload Guide</span>
                </div>
              </Dialog.Title>
              <button
                onClick={handleClose}
                className="text-white/70 hover:text-white rounded-full p-1 hover:bg-white/10 transition-colors hover:cursor-pointer"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <p className="text-white/80 text-sm mt-1">
              Follow these guidelines for best results
            </p>
          </div>

          {/* Content */}
          <div className="p-6 bg-white dark:bg-gray-900 overflow-y-auto max-h-[70vh]">
            {/* Guidelines section */}
            <div className="mb-6">
              <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <span className="bg-purple-100 dark:bg-purple-900/30 rounded-full p-1 mr-2">
                  <CheckCircleIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </span>
                Upload requirements
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <li className="flex items-start">
                  <div className="min-w-5 text-purple-600 dark:text-purple-400 mr-2">
                    •
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">
                    Show the front of the product clearly
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="min-w-5 text-purple-600 dark:text-purple-400 mr-2">
                    •
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">
                    Center the product in frame
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="min-w-5 text-purple-600 dark:text-purple-400 mr-2">
                    •
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">
                    Ensure good, even lighting
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="min-w-5 text-purple-600 dark:text-purple-400 mr-2">
                    •
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">
                    No hands or people in shot
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="min-w-5 text-purple-600 dark:text-purple-400 mr-2">
                    •
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">
                    White or transparent background
                  </span>
                </li>
              </ul>
            </div>

            {/* Example images section */}
            <div>
              <h4 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <span className="bg-green-100 dark:bg-green-900/30 rounded-full p-1 mr-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                </span>
                Example images
              </h4>

              {/* Perfect 2x2 grid with aspect ratio constraints */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Using aspect-square for perfect squares regardless of image dimensions */}
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 shadow-md group relative">
                  <img
                    src="https://imagedelivery.net/rd3g2mSfZS3u7kb9ijW_yw/0c55f1f7-fdc1-4d73-3a9c-8c5871ddf400/public"
                    alt="Example product 1"
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <a
                      href="https://imagedelivery.net/rd3g2mSfZS3u7kb9ijW_yw/0c55f1f7-fdc1-4d73-3a9c-8c5871ddf400/public"
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 bg-white/90 rounded-full"
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-900" />
                    </a>
                  </div>
                </div>

                <div className="aspect-square rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 shadow-md group relative">
                  <img
                    src="https://imagedelivery.net/rd3g2mSfZS3u7kb9ijW_yw/f5a29b13-6e1a-4980-0047-d01406e16700/public"
                    alt="Example product 2"
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <a
                      href="https://imagedelivery.net/rd3g2mSfZS3u7kb9ijW_yw/f5a29b13-6e1a-4980-0047-d01406e16700/public"
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 bg-white/90 rounded-full"
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-900" />
                    </a>
                  </div>
                </div>

                <div className="aspect-square rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 shadow-md group relative">
                  <img
                    src="https://imagedelivery.net/rd3g2mSfZS3u7kb9ijW_yw/0e6a2eae-f9de-4323-19cc-f0c1a6cfd200/public"
                    alt="Example product 3"
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <a
                      href="https://imagedelivery.net/rd3g2mSfZS3u7kb9ijW_yw/0e6a2eae-f9de-4323-19cc-f0c1a6cfd200/public"
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 bg-white/90 rounded-full"
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-900" />
                    </a>
                  </div>
                </div>

                <div className="aspect-square rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 shadow-md group relative">
                  <img
                    src="https://imagedelivery.net/rd3g2mSfZS3u7kb9ijW_yw/8047d984-0513-42b8-e1b7-31b770e92900/public"
                    alt="Example product 4"
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <a
                      href="https://imagedelivery.net/rd3g2mSfZS3u7kb9ijW_yw/8047d984-0513-42b8-e1b7-31b770e92900/public"
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 bg-white/90 rounded-full"
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-900" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer with glass effect */}
          <div className="p-5 border-t border-gray-200/30 dark:border-gray-700/30 flex justify-between items-center backdrop-blur-sm bg-white/80 dark:bg-gray-900/80">
            <label className="flex items-center group cursor-pointer">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-10 h-5 bg-gray-200 dark:bg-gray-700 rounded-full transition-colors duration-200 ease-in-out ${
                    dontShowAgain ? "bg-purple-500 dark:bg-purple-600" : ""
                  }`}
                ></div>
                <div
                  className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out transform ${
                    dontShowAgain ? "translate-x-5" : ""
                  }`}
                ></div>
              </div>
              <span className="ml-3 text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors">
                Don't show again
              </span>
            </label>

            <button
              onClick={handleClose}
              className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg shadow-sm hover:from-purple-700 hover:to-indigo-700 transform hover:translate-y-[-1px] transition-all duration-200 hover:cursor-pointer"
            >
              Got it
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
